import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  Zap,
  Users,
  HeadphonesIcon,
  Upload,
  ShieldCheck,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';
import HeroSection from '../components/HeroSection';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

const benefits = [
  {
    icon: <Zap size={22} className="text-amber-500" />,
    title: 'Fast and Transparent Underwriting',
    desc: 'Our team reviews submissions quickly and communicates clearly throughout the process.',
  },
  {
    icon: <Users size={22} className="text-amber-500" />,
    title: 'Real-Time Deal Tracking',
    desc: 'Partners can track submissions, approvals, and funding activity through the partner workflow.',
  },
  {
    icon: <HeadphonesIcon size={22} className="text-amber-500" />,
    title: 'Dedicated Support',
    desc: 'Work with a responsive team that understands partner relationships and ongoing support needs.',
  },
  {
    icon: <CheckCircle size={22} className="text-amber-500" />,
    title: 'Quick Commissions',
    desc: 'Receive timely commission processing with a clear and consistent partner experience.',
  },
  {
    icon: <ShieldCheck size={22} className="text-amber-500" />,
    title: 'Long-Term Relationship Focus',
    desc: 'Bizfund is built around consistency, trust, and long-term partnerships.',
  },
  {
    icon: <TrendingUp size={22} className="text-amber-500" />,
    title: 'Close More Deals',
    desc: 'Aggressive buy rates and fast decisions help you win and fund more deals.',
  },
];

const steps = [
  {
    step: '01',
    title: 'Complete the Partner Form',
    desc: 'Submit your company details, contact information, and required onboarding documents.',
  },
  {
    step: '02',
    title: 'Receive Your ISO Agreement',
    desc: 'We will send your ISO Agreement through DocuSign for electronic signature.',
  },
  {
    step: '03',
    title: 'Get Set Up in Our CRM',
    desc: 'Once signed, our team will complete your setup and prepare your partner workflow.',
  },
  {
    step: '04',
    title: 'Start Submitting Deals',
    desc: 'Begin sending opportunities and tracking activity with Bizfund.',
  },
];

const hearAboutOptions = [
  "I don't have a representative",
  'Leor Friedman',
  'David Aliev',
  'Danny Warren',
  'Sam Anzaroot',
  'Chanel Caouette',
  'Tim Bonventre',
  'Merie Lopez',
];

export default function Partner() {
  const [form, setForm] = useState({
    companyName: '',
    contactPerson: '',
    title: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    taxId: '',
    stateOfIncorporation: '',
    yearsInBusiness: '',
    references: '',
    funderReferenceCompanyName: '',
    funderReferenceContactName: '',
    funderReferencePhoneNumber: '',
    funderReferenceEmailAddress: '',
    heardAboutUs: '',
    agreeTerms: false,
    agreeEsign: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.agreeTerms || !form.agreeEsign) {
      alert('Please accept the required agreements before submitting.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formData = new FormData(e.target);
      const response = await fetch('/api/partner-registration', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error || 'Server error while submitting your registration.');
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Partner form submission failed:', error);
      setSubmitError('We were unable to send your registration at this time. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-shadow shadow-[0_2px_10px_rgba(15,23,42,0.03)]';

  const fileInputClass =
    'w-full cursor-pointer pl-10 pr-4 py-3 rounded-2xl border border-gray-200 bg-white text-sm text-gray-700 shadow-[0_2px_10px_rgba(15,23,42,0.03)] file:mr-4 file:cursor-pointer file:border-0 file:bg-transparent file:text-sm';

  return (
    <main className="bg-[#F8F7F4] text-gray-900">
      <HeroSection
        tag="Partner With Bizfund"
        title="Partner With Bizfund"
        subtitle="We’re not just looking for submissions. We’re looking for partners. Bizfund works closely with brokers and referral partners to build long term relationships backed by fast decisions, transparent communication, and a team committed to growth. Complete the form below to partner with us."
        image="/images/offline/photo-1556761175-4b46a572b786.jpg"
        sectionClassName="min-h-[500px] h-[72svh] md:min-h-[520px] md:h-[68vh]"
      >
        <div className="mx-auto mt-6 max-w-4xl md:mt-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-5 rounded-[28px] border border-white/10 bg-white/10 px-5 py-5 text-left backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between sm:px-6 md:px-8 md:py-6"
          >
            <div>
              <h3 className="text-xl font-bold text-white sm:text-2xl">
                Ready to join the Bizfund partner network?
              </h3>
              <p className="mt-2 text-sm text-slate-300">
                Complete your registration to get started today.
              </p>
            </div>
            <a
              href="https://signup.bizfund.com/loginname/register"
              className="flex items-center gap-2 whitespace-nowrap rounded-full bg-amber-400 px-8 py-3.5 font-semibold text-[#0F172A] shadow-[0_12px_28px_rgba(251,191,36,0.18)] transition-all duration-300 hover:bg-amber-300"
            >
              Complete Registration <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </HeroSection>

      {/* Why Become a Preferred Partner */}
      <section className="page-section bg-[#F8F7F4]">
        <div className="page-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="section-intro-wide text-center"
          >
            <span className="text-sm uppercase tracking-widest font-semibold bg-gradient-to-r from-sky-500 via-blue-600 to-blue-700 bg-clip-text text-transparent">
              LOYAL TO YOUR GROWTH
            </span>
            <h2 className="text-4xl font-bold mt-2 text-gray-900">
              Why Become a Preferred Partner
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              Fast decisions, transparent underwriting, and responsive support.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-6"
          >
            {benefits.map((b) => (
              <motion.div
                key={b.title}
                variants={fadeUp}
                className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-[0_10px_35px_rgba(15,23,42,0.06)] transition-all hover:shadow-[0_16px_42px_rgba(15,23,42,0.10)] md:p-7"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#0F172A] flex items-center justify-center mb-4">
                  {b.icon}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{b.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Registration Form */}
      <section
        id="complete-registration"
        className="bg-[#F8F7F4] pb-12 pt-6 md:pb-16 md:pt-8 lg:pb-20 lg:pt-10"
      >
        <div className="page-container">
          <div className="grid gap-8 lg:grid-cols-5 lg:gap-10 xl:gap-12">
            {/* Sidebar */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-5 lg:col-span-2 md:space-y-6"
            >
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Partner Registration
                </h3>
                <p className="text-gray-500 leading-relaxed">
                  Complete the registration form below to begin the onboarding process
                  with Bizfund.
                </p>
              </div>

              <div className="space-y-3 rounded-[28px] border border-[#E8E2D8] bg-gradient-to-br from-[#F6F1E8] via-[#F3ECE0] to-[#EEE3D1] p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] md:p-6">
                <h4 className="font-bold text-[#0F172A]">What You’ll Need</h4>
                {[
                  'Company and contact information',
                  'Tax ID / EIN',
                  'State of incorporation',
                  'Years in business',
                  'W9',
                  'Driver’s License',
                  'Voided Check',
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2 text-sm text-[#5B6473]"
                  >
                    <CheckCircle size={14} className="text-amber-600 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

              <div className="rounded-[28px] overflow-hidden border border-gray-200 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
                <img
                  src="/images/offline/photo-1521791136064-7986c2920216.jpg"
                  alt="Business partners"
                  className="w-full h-56 object-cover"
                />
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className="rounded-[32px] border border-gray-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.07)] sm:p-8 md:p-10">
                {!submitted ? (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Complete Registration
                    </h3>
                    <p className="mb-7 text-sm text-gray-500 md:mb-8">
                      Please provide your details below. Required onboarding
                      documents can be uploaded as part of registration.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Company Name *
                          </label>
                          <input
                            name="companyName"
                            value={form.companyName}
                            onChange={handleChange}
                            required
                            placeholder="Biz Capital LLC"
                            className={inputClass}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Contact Person *
                          </label>
                          <input
                            name="contactPerson"
                            value={form.contactPerson}
                            onChange={handleChange}
                            required
                            placeholder="John Smith"
                            className={inputClass}
                          />
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Title *
                          </label>
                          <input
                            name="title"
                            value={form.title}
                            onChange={handleChange}
                            required
                            placeholder="Managing Partner"
                            className={inputClass}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Email *
                          </label>
                          <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                            placeholder="john@company.com"
                            className={inputClass}
                          />
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Phone *
                          </label>
                          <input
                            name="phone"
                            type="tel"
                            value={form.phone}
                            onChange={handleChange}
                            required
                            placeholder="(555) 000-0000"
                            className={inputClass}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Website
                          </label>
                          <input
                            name="website"
                            value={form.website}
                            onChange={handleChange}
                            placeholder="https://yourcompany.com"
                            className={inputClass}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Address *
                        </label>
                        <input
                          name="address"
                          value={form.address}
                          onChange={handleChange}
                          required
                          placeholder="123 Main Street, City, State"
                          className={inputClass}
                        />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Tax ID / EIN *
                          </label>
                          <input
                            name="taxId"
                            value={form.taxId}
                            onChange={handleChange}
                            required
                            placeholder="12-3456789"
                            className={inputClass}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            State of Incorporation *
                          </label>
                          <input
                            name="stateOfIncorporation"
                            value={form.stateOfIncorporation}
                            onChange={handleChange}
                            required
                            placeholder="Florida"
                            className={inputClass}
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Years in Business *
                          </label>
                          <input
                            name="yearsInBusiness"
                            value={form.yearsInBusiness}
                            onChange={handleChange}
                            required
                            placeholder="5"
                            className={inputClass}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Select your ISO representative
                          </label>
                          <select
                            name="heardAboutUs"
                            value={form.heardAboutUs}
                            onChange={handleChange}
                            className={`${inputClass} bg-white`}
                          >
                            <option value="">Select</option>
                            {hearAboutOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="space-y-4 pt-2">
                        <h4 className="text-base font-bold text-gray-900">
                          Funder Reference
                        </h4>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Company Name *
                            </label>
                            <input
                              name="funderReferenceCompanyName"
                              value={form.funderReferenceCompanyName}
                              onChange={handleChange}
                              required
                              placeholder="ABC Funding"
                              className={inputClass}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Contact Name *
                            </label>
                            <input
                              name="funderReferenceContactName"
                              value={form.funderReferenceContactName}
                              onChange={handleChange}
                              required
                              placeholder="Jane Smith"
                              className={inputClass}
                            />
                          </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Phone Number *
                            </label>
                            <input
                              name="funderReferencePhoneNumber"
                              type="tel"
                              value={form.funderReferencePhoneNumber}
                              onChange={handleChange}
                              required
                              placeholder="(555) 000-0000"
                              className={inputClass}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Email Address *
                            </label>
                            <input
                              name="funderReferenceEmailAddress"
                              type="email"
                              value={form.funderReferenceEmailAddress}
                              onChange={handleChange}
                              required
                              placeholder="jane@company.com"
                              className={inputClass}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Uploads */}
                      <div className="space-y-4 pt-2">
                        <h4 className="text-base font-bold text-gray-900">
                          Upload Documents
                        </h4>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              W9 *
                            </label>
                            <div className="relative">
                              <Upload size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-600" />
                              <input
                                name="w9"
                                type="file"
                                required
                                className={fileInputClass}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Driver’s License *
                            </label>
                            <div className="relative">
                              <Upload size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-600" />
                              <input
                                name="driversLicense"
                                type="file"
                                required
                                className={fileInputClass}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Voided Check *
                            </label>
                            <div className="relative">
                              <Upload size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-600" />
                              <input
                                name="voidedCheck"
                                type="file"
                                required
                                className={fileInputClass}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Optional Company Documents
                            </label>
                            <div className="relative">
                              <Upload size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-600" />
                              <input
                                name="optionalDocuments"
                                type="file"
                                className={fileInputClass}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Agreements */}
                      <div className="space-y-3 pt-2">
                        <label className="flex items-start gap-3 text-sm text-gray-600">
                          <input
                            type="checkbox"
                            name="agreeTerms"
                            checked={form.agreeTerms}
                            onChange={handleChange}
                            className="mt-1 accent-amber-500"
                          />
                          <span>
                            I agree to Bizfund’s Terms and Privacy Policy.
                          </span>
                        </label>

                        <label className="flex items-start gap-3 text-sm text-gray-600">
                          <input
                            type="checkbox"
                            name="agreeEsign"
                            checked={form.agreeEsign}
                            onChange={handleChange}
                            className="mt-1 accent-amber-500"
                          />
                          <span>
                            I consent to e-signing through DocuSign.
                          </span>
                        </label>
                      </div>

                      {submitError && (
                        <p className="text-sm text-red-600">{submitError}</p>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 text-white font-bold py-4 rounded-full transition-all duration-300 text-base shadow-[0_12px_28px_rgba(37,99,235,0.22)]"
                      >
                        {isSubmitting ? 'Sending...' : 'Apply for Funding'}
                      </button>
                    </form>
                  </>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-16"
                  >
                    <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6 ring-1 ring-emerald-100">
                      <CheckCircle size={40} className="text-emerald-600" />
                    </div>

                    <h3 className="text-3xl font-bold text-gray-900 mb-3">
                      Thank You for Registering
                    </h3>

                    <p className="text-gray-500 leading-relaxed max-w-lg mx-auto">
                      You’ll receive an email from DocuSign shortly to complete your
                      ISO Agreement. If you don’t see it, please check your spam
                      folder or contact{' '}
                      <a
                        href="mailto:partners@bizfund.com"
                        className="text-amber-700 hover:text-amber-800 font-medium"
                      >
                        partners@bizfund.com
                      </a>.
                    </p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
